/**
 * API Service
 * Centralized HTTP client with error handling and interceptors
 */

import axios, { AxiosInstance, AxiosRequestConfig, AxiosResponse } from 'axios';
import { API_CONFIG, STORAGE_KEYS } from '../config';
import { storageUtils, logger, CookieManager } from '../utils';
import type { ApiResponse, ApiError } from '../types';

// Extend Axios config to include our metadata
declare module 'axios' {
  interface InternalAxiosRequestConfig {
    metadata?: {
      apiCallLog?: ApiCallLog;
    };
  }
}

// API Call tracking for debug page
export interface ApiCallLog {
  id: string;
  timestamp: number;
  method: string;
  url: string;
  status: number | 'pending' | 'error';
  duration: number | null;
  headers: Record<string, string>;
  request?: unknown;
  response?: unknown;
  error?: string;
}

// Options for the GET response cache - opt out per call with { skipCache: true },
// or tune freshness with { cacheTtlMs }.
export interface CacheOptions {
  skipCache?: boolean;
  cacheTtlMs?: number;
}

interface CacheEntry {
  data: ApiResponse<unknown>;
  expiresAt: number;
}

class ApiService {
  private client: AxiosInstance;
  private static apiCallLogs: ApiCallLog[] = [];
  private static maxLogs = 100;

  // In-memory GET response cache, keyed by request URL. Lives only for the
  // page's JS lifetime (a hard refresh or new tab starts empty), so
  // navigating between screens doesn't re-fetch data that was already
  // loaded moments ago, without letting anything go stale for long.
  private static getCache = new Map<string, CacheEntry>();
  // Dedupes concurrent identical GETs (e.g. two components mounting at once
  // and both requesting the same resource) into a single in-flight request.
  private static inFlight = new Map<string, Promise<ApiResponse<unknown>>>();
  private static readonly DEFAULT_CACHE_TTL_MS = 60_000;

  private static cacheKey(url: string, config?: AxiosRequestConfig): string {
    const params = config?.params ? JSON.stringify(config.params) : '';
    return `${url}::${params}`;
  }

  /** Drop cached GETs whose URL falls under the same top-level resource as a
   * mutation (e.g. a POST to /blogs/123/archive invalidates cached /blogs*
   * reads) so writes are never masked by a stale cached read. */
  private static invalidateCacheFor(url: string): void {
    const resource = url.split('?')[0].split('/').filter(Boolean)[0];
    if (!resource) {
      ApiService.getCache.clear();
      return;
    }
    for (const key of ApiService.getCache.keys()) {
      if (key.split('?')[0].includes(`/${resource}`) || key.startsWith(resource)) {
        ApiService.getCache.delete(key);
      }
    }
  }

  /** Clears the entire GET cache - call after actions with broad side effects
   * (e.g. logout) where per-resource invalidation isn't precise enough. */
  static clearCache(): void {
    ApiService.getCache.clear();
    ApiService.inFlight.clear();
  }

  constructor() {
    this.client = axios.create({
      baseURL: API_CONFIG.baseURL,
      timeout: API_CONFIG.timeout,
      headers: API_CONFIG.headers,
    });

    this.setupInterceptors();
  }

  // Expose baseURL for debugging
  getBaseURL(): string {
    return API_CONFIG.baseURL;
  }

  private setupInterceptors(): void {
    // Request interceptor
    this.client.interceptors.request.use(
      (config) => {
        // Create API call log entry
        const apiCallLog = this.createApiCallLog(config);
        config.metadata = { apiCallLog };

        // Add common headers
        config.headers['Content-Type'] = 'application/json';
        config.headers['Accept'] = 'application/json';

        // Add Project ID from environment - REQUIRED for all requests
        const projectId = API_CONFIG.projectId;
        if (projectId) {
          config.headers['X-Project-Id'] = projectId;
          logger.debug('Added project ID header', 'ApiService', { projectId });
        } else {
          logger.warn('Project ID not found - server may reject request', 'ApiService');
        }

        // Add auth token if available
        // UserService stores token in cookies, so read from cookies first, then fallback to localStorage
        let token = CookieManager.get(STORAGE_KEYS.AUTH_TOKEN);
        if (!token) {
          token = storageUtils.getItem(STORAGE_KEYS.AUTH_TOKEN);
        }
        if (token) {
          config.headers.Authorization = `Bearer ${token}`;
          logger.debug('Added auth token to request', 'ApiService', { 
            hasToken: !!token,
            tokenLength: token.length 
          });
        } else {
          logger.debug('No auth token found', 'ApiService');
        }

        // Update the log with final headers
        apiCallLog.headers = { ...config.headers } as Record<string, string>;
        if (apiCallLog.headers.Authorization) {
          apiCallLog.headers.Authorization = '[REDACTED]';
        }

        // Start timing and log the call
        ApiService.logApiCall(apiCallLog);

        logger.debug('API Request', 'ApiService', {
          method: config.method,
          url: config.url,
          data: config.data,
          headers: {
            'X-Project-Id': config.headers['X-Project-Id'],
            'Authorization': config.headers.Authorization ? '[REDACTED]' : 'None'
          }
        });

        return config;
      },
      (error) => {
        logger.error('API Request Error', 'ApiService', { error });
        return Promise.reject(error);
      }
    );

    // Response interceptor
    this.client.interceptors.response.use(
      (response: AxiosResponse<ApiResponse>) => {
        // Update API call log with response
        if (response.config.metadata?.apiCallLog) {
          const log = response.config.metadata.apiCallLog as ApiCallLog;
          log.status = response.status;
          log.duration = Date.now() - log.timestamp;
          log.response = response.data;
        }

        logger.debug('API Response', 'ApiService', {
          status: response.status,
          data: response.data,
        });

        return response;
      },
      async (error) => {
        // Update API call log with error
        if (error.config?.metadata?.apiCallLog) {
          const log = error.config.metadata.apiCallLog as ApiCallLog;
          log.status = error.response?.status || 'error';
          log.duration = Date.now() - log.timestamp;
          log.error = error.message;
          if (error.response?.data) {
            log.response = error.response.data;
          }
        }

        // const originalRequest = error.config; // Unused

        // Handle 401 unauthorized - session expired, clear tokens and let caller handle redirect
        if (error.response?.status === 401) {
          // Clear authentication tokens from both cookies and localStorage
          CookieManager.remove(STORAGE_KEYS.AUTH_TOKEN, { path: '/' });
          storageUtils.removeItem(STORAGE_KEYS.AUTH_TOKEN);
          // Note: REFRESH_TOKEN removed from system, but clearing for safety if any exist
          if (storageUtils.getItem(STORAGE_KEYS.REFRESH_TOKEN)) {
            storageUtils.removeItem(STORAGE_KEYS.REFRESH_TOKEN);
          }
          ApiService.clearCache();
          logger.warn('Authentication failed - cleared tokens', 'ApiService');
        }
        
        // Handle CORS errors
        if (!error.response && error.message && (
          error.message.includes('CORS') || 
          error.message.includes('Access-Control') ||
          error.message.includes('Network Error') ||
          error.code === 'ERR_NETWORK'
        )) {
          logger.error('CORS or Network Error', 'ApiService', {
            message: error.message,
            url: error.config?.url,
            baseURL: error.config?.baseURL,
            code: error.code
          });
          
          // Return a user-friendly error
          const corsError: ApiError = {
            type: 'cors_error',
            message: 'Unable to connect to the server. Please check your internet connection or contact support if the problem persists.',
            status_code: 0,
            details: {
              original_error: error.message,
              url: error.config?.url
            }
          };
          
          return Promise.reject({
            success: false,
            data: undefined,
            error: corsError,
            timestamp: new Date().toISOString()
          } as ApiResponse<undefined>);
        }

        logger.error('API Response Error', 'ApiService', {
          status: error.response?.status,
          data: error.response?.data,
          message: error.message,
        });

        return Promise.reject(this.handleError(error));
      }
    );
  }


  private handleError(error: unknown): ApiError {
    if (axios.isAxiosError(error)) {
      if (error.response) {
        // Server responded with error status
        return {
          message: error.response.data?.message || 'Server error occurred',
        };
      } else if (error.request) {
        // Network error
        return {
          message: 'Network error. Please check your connection.',
        };
      }
    }
    
    // Other error
    return {
      message: error instanceof Error ? error.message : 'An unexpected error occurred',
    };
  }

  // HTTP Methods
  async get<T = unknown>(url: string, config?: AxiosRequestConfig & CacheOptions): Promise<ApiResponse<T>> {
    const { skipCache, cacheTtlMs, ...axiosConfig } = config || {};
    const key = ApiService.cacheKey(url, axiosConfig);

    if (!skipCache) {
      const cached = ApiService.getCache.get(key);
      if (cached && cached.expiresAt > Date.now()) {
        return cached.data as ApiResponse<T>;
      }
      const pending = ApiService.inFlight.get(key);
      if (pending) {
        return pending as Promise<ApiResponse<T>>;
      }
    }

    const request = this.client.get<ApiResponse<T>>(url, axiosConfig).then((response) => {
      if (!skipCache && response.data?.success !== false) {
        ApiService.getCache.set(key, {
          data: response.data,
          expiresAt: Date.now() + (cacheTtlMs ?? ApiService.DEFAULT_CACHE_TTL_MS),
        });
      }
      return response.data;
    }).finally(() => {
      ApiService.inFlight.delete(key);
    });

    if (!skipCache) {
      ApiService.inFlight.set(key, request);
    }
    return request;
  }

  async post<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.post<ApiResponse<T>>(url, data, config);
    ApiService.invalidateCacheFor(url);
    return response.data;
  }

  async put<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.put<ApiResponse<T>>(url, data, config);
    ApiService.invalidateCacheFor(url);
    return response.data;
  }

  async patch<T = unknown>(url: string, data?: unknown, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.patch<ApiResponse<T>>(url, data, config);
    ApiService.invalidateCacheFor(url);
    return response.data;
  }

  async delete<T = unknown>(url: string, config?: AxiosRequestConfig): Promise<ApiResponse<T>> {
    const response = await this.client.delete<ApiResponse<T>>(url, config);
    ApiService.invalidateCacheFor(url);
    return response.data;
  }

  // File upload
  async uploadFile<T = unknown>(
    url: string, 
    file: File, 
    onProgress?: (progress: number) => void,
    additionalData?: Record<string, string>
  ): Promise<ApiResponse<T>> {
    const formData = new FormData();
    formData.append('file', file);
    
    // Add additional form data if provided
    if (additionalData) {
      Object.entries(additionalData).forEach(([key, value]) => {
        formData.append(key, value);
      });
    }

    const config: AxiosRequestConfig = {
      headers: {
        'Content-Type': 'multipart/form-data',
      } as Record<string, string>,
      onUploadProgress: (progressEvent) => {
        if (onProgress && progressEvent.total) {
          const progress = Math.round((progressEvent.loaded * 100) / progressEvent.total);
          onProgress(progress);
        }
      },
    };

    const response = await this.client.post<ApiResponse<T>>(url, formData, config);
    return response.data;
  }

  // Debug methods for development
  static getApiCallLogs(): ApiCallLog[] {
    return ApiService.apiCallLogs;
  }

  static clearApiCallLogs(): void {
    ApiService.apiCallLogs = [];
  }

  // Instance methods that call the static methods for easier access
  getApiCallLogs(): ApiCallLog[] {
    return ApiService.getApiCallLogs();
  }

  clearApiCallLogs(): void {
    ApiService.clearApiCallLogs();
  }

  clearCache(): void {
    ApiService.clearCache();
  }

  private static logApiCall(log: ApiCallLog): void {
    ApiService.apiCallLogs.unshift(log);
    if (ApiService.apiCallLogs.length > ApiService.maxLogs) {
      ApiService.apiCallLogs = ApiService.apiCallLogs.slice(0, ApiService.maxLogs);
    }
  }

  private createApiCallLog(config: AxiosRequestConfig): ApiCallLog {
    return {
      id: Date.now().toString() + Math.random().toString(36).substr(2, 9),
      timestamp: Date.now(),
      method: (config.method || 'GET').toUpperCase(),
      url: config.url || '',
      status: 'pending',
      duration: null,
      headers: config.headers as Record<string, string>,
      request: config.data,
      response: undefined,
      error: undefined,
    };
  }
}

// Export singleton instance
export const apiService = new ApiService();
export default apiService;
